/**
 * @fileoverview Test Cases Table Component
 * @description Sortable, filterable data table for auto-generated test cases
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { Search, Download } from 'lucide-react';

const STATE_COLORS = {
  Ready: { bg: '#DCFCE7', color: '#166534' },
  Design: { bg: '#FEF3C7', color: '#92400E' },
  Closed: { bg: '#F3F4F6', color: '#374151' },
  Active: { bg: '#DBEAFE', color: '#1E40AF' },
};

const columns = [
  { id: 'id', label: 'ID', minWidth: 70, align: 'left' },
  { id: 'project', label: 'Project', minWidth: 160 },
  { id: 'title', label: 'Title', minWidth: 260 },
  { id: 'autoGeneratedTests', label: 'Auto Generated Tests', minWidth: 160 },
  { id: 'testDesignTime', label: 'Design Time', minWidth: 100, align: 'right' },
  { id: 'state', label: 'State', minWidth: 90 },
  { id: 'assignedTo', label: 'Assigned To', minWidth: 140 },
  { id: 'createdDate', label: 'Created', minWidth: 110 },
];

const TestCasesTable = ({ testCases = [], loading }) => {
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('createdDate');
  const [order, setOrder] = useState('desc');

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return testCases
      .filter(
        (tc) =>
          !q ||
          (tc.title || '').toLowerCase().includes(q) ||
          (tc.project || '').toLowerCase().includes(q) ||
          (tc.assignedTo || '').toLowerCase().includes(q) ||
          String(tc.id).includes(q) ||
          (tc.autoGeneratedTests || '').toLowerCase().includes(q)
      )
      .sort((a, b) => {
        const aVal = a[orderBy] ?? '';
        const bVal = b[orderBy] ?? '';
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
      });
  }, [testCases, search, orderBy, order]);

  const handleExportCsv = () => {
    if (!filtered.length) return;
    const header = columns.map((c) => c.label).join(',');
    const rows = filtered.map((tc) =>
      columns
        .map((c) => {
          let val = tc[c.id] ?? '';
          if (c.id === 'createdDate' && val) {
            val = new Date(val).toLocaleDateString();
          }
          // Escape CSV values
          val = String(val).replace(/"/g, '""');
          return `"${val}"`;
        })
        .join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'AutoGeneratedTestCases.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 3, border: '1px solid #E5E7EB', overflow: 'hidden' }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #E5E7EB',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <TextField
          size="small"
          placeholder="Search test cases..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="#9CA3AF" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            {filtered.length} of {testCases.length} records
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download size={14} />}
            onClick={handleExportCsv}
            disabled={!filtered.length}
            sx={{ textTransform: 'none' }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  sx={{
                    minWidth: col.minWidth,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#6B7280',
                    backgroundColor: '#F9FAFB',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    {loading ? 'Loading...' : 'No test cases found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((tc) => {
                const stateStyle = STATE_COLORS[tc.state] || { bg: '#F3F4F6', color: '#374151' };
                return (
                  <TableRow
                    key={tc.id}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: '#0097A9' }}>
                      {tc.id}
                    </TableCell>
                    <TableCell>{tc.project}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={tc.title}
                    >
                      {tc.title}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tc.autoGeneratedTests}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: '#EDE9FE',
                          color: '#5B21B6',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {tc.testDesignTime != null ? tc.testDesignTime : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tc.state}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: stateStyle.bg,
                          color: stateStyle.color,
                        }}
                      />
                    </TableCell>
                    <TableCell>{tc.assignedTo || '—'}</TableCell>
                    <TableCell>
                      {tc.createdDate
                        ? new Date(tc.createdDate).toLocaleDateString()
                        : '—'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TestCasesTable;
